import {CohortDataset, Query} from "../../containers/HealthcareUsageReport/types/CohortQuery";
import {generatePath, match as Match} from "react-router-dom";
import {History, Location} from "history";
import {AppDispatch} from "../../configureStore";
import {Col, message, Row} from "antd";
import classNames from "classnames";
import styles from "./ListQueries.module.scss";
import {CohortStatus} from "./CohortStatus";
import EditFunctions from "./actions/EditFunctions";
import ExportPWVButton from "./actions/ExportPWVButton";
import {ViewDescriptiveStatsButton} from "./actions/ViewDescriptiveStatsButton";
import ExportQBButton from "./actions/ExportQBButton";
import React from "react";
import {buildStudyStatsRoute, Routes, STATS} from "../../pages/routes";
import _ from "lodash";
import {setCurrentReportAndRefresh} from "../../containers/HealthcareUsageReport/actions";
import {emptyCohortReport} from "../../containers/HealthcareUsageReport/reducers";
import {setCurrentQuery} from "../../containers/CohortBuilder/cohortBuilderSlice";
import {setCurrentPathwayAndRefresh} from "../../containers/PathwayViewer/actions";
import {FINISHED_STATUS} from "../../containers/CohortBuilder/cohortStatus";
import {findDataset} from "../../containers/CohortBuilder/DatasetPicker/DatasetPickerUtil";

export type QueryCardProps = {
    query: Query,
    selectedQuery: Query | undefined,
    projectMatch: Match<{projectId: string}>,
    history: History,
    location: Location,
    dispatch: AppDispatch
    permittedDatasets: CohortDataset[]
}

const isHRUPage = (location: Location) => location.pathname === Routes.MyHRU

const isPWVPage = (location: Location) => {
    return location.pathname === Routes.MyPWV
}

const getCount = (query: Query) => {
    if (!_.isNil(query.sparkQuery) && !_.isNil(query.sparkQuery.response)) {
        const response = JSON.parse(query.sparkQuery.response)
        if (!_.isNil(response)) {
            return <span className={styles.count}>{`${parseInt(response.count).toLocaleString()}`}</span>
        }
    }
    return null
}

const getDate = (query: Query) => {
    if (!_.isNil(query.createdOn)) {
        const timestamp = query.createdOn
        const date = new Date(timestamp)

        var timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
        const timeString = date.toLocaleTimeString('en-GB', timeOptions)

        var dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }
        const dateString = date.toLocaleDateString('en-GB', dateOptions)
        return `${dateString}, ${timeString}`
    } else {
        return null
    }
}

const fire = (query: Query, history: History, projectMatch: Match, dispatch: AppDispatch) => {
    const path =  history.location.pathname

    if (path === Routes.MyHRU) {
        if (query.trackable) {
            dispatch(setCurrentReportAndRefresh(emptyCohortReport(query.id)))
            message.info('Cohort selected for HRU.');
        } else {
            message.info('This cohort is not trackable and therefore be cannot used in HRU.');
        }
    } else if (path === Routes.MyPWV) {
        if (query.trackable) {
            if (query.pathwayStatus === "COMPLETE") {
                dispatch(setCurrentQuery(query.id))
                dispatch(setCurrentPathwayAndRefresh())
                message.info('Cohort selected for PWV.');
            } else {
                message.info('This cohorts pathways have not been generated and therefore it is not possible to create a pathway to explore.');
            }
        } else {
            message.info('This cohort is not trackable and therefore it is not possible to create a pathway to explore.');
        }
    } else {
        if (path.endsWith(STATS)) {
            if (query.sparkQuery && FINISHED_STATUS === query.sparkQuery.status) {
                // @ts-ignore
                history.push(buildStudyStatsRoute(projectMatch.params.projectId, query.id))
            } else {
                message.info('Cannot view Descriptive Stats for this cohort.')
            }
        } else {
            if (query.id > 0) {
                history.push(generatePath(Routes.Project, {
                    ...projectMatch.params,
                    studyId: query.id,
                    study: 'study',
                    subpage: undefined
                }))
            } else {
                history.push(generatePath(Routes.Project, {
                    ...projectMatch.params,
                    studyId: undefined,
                    study: undefined,
                    subpage: undefined
                }))
                dispatch(setCurrentQuery(query.id))
            }
        }
    }
}

export const getDatasetPermission = (dataset: CohortDataset, permittedDatasets: CohortDataset[]) => {
    return permittedDatasets.includes(dataset)
}

export const QueryCard = (
    {query, selectedQuery, projectMatch, history, location, dispatch, permittedDatasets} : QueryCardProps
) => {

    const dataset = findDataset(query.dataset)!
    const hasDatasetPermission = getDatasetPermission(dataset.key as CohortDataset, permittedDatasets)
    const canEditOnThisPage = !isHRUPage(location) || !isPWVPage(location)
    const isQueryIncomplete = _.isNil(query.sparkQuery)

    const isQueryVisible = !hasDatasetPermission && isQueryIncomplete;
    if (isQueryVisible) {
        return null
    }

    return <Row className={classNames(styles['query'], {[styles['active']]: selectedQuery?.id === query.id})}>
        <Row
            className={styles["info"]}
            key={query.id}
            onClick={() => fire(query, history, projectMatch, dispatch)}
        >
            <Row className={styles['query-row']}>
                <Col span={18}><span className={styles['title']} data-testid={`query-list-title-${query.id}`}>{query.title}</span></Col>
                <Col span={6} data-testid={`query-list-count-${query.id}`}>{getCount(query)}</Col>
            </Row>
            <Row>
                <Col span={24} className={styles["date"]}>
                    {getDate(query)}
                    <span className={styles['dataset']}>{dataset.icon} {dataset.code} </span>
                    <CohortStatus query={query} />
                </Col>
            </Row>
        </Row >
        {!hasDatasetPermission ? null :
            <Row className={styles["actions-bar"]} wrap={false}>
                {canEditOnThisPage ? <EditFunctions query={query}/> : null}
                <Col span={6}/>
                <Col span={8}>
                    <Row gutter={[16, 4]} justify="end" wrap={false}>
                        <Col data-testid={`query-list-pwv-${query.id}`}><ExportPWVButton query={query}/></Col>
                        <Col data-testid={`query-list-stats-${query.id}`}>
                            <ViewDescriptiveStatsButton key={`stats-button-${query.id}`} projectId={projectMatch?.params?.projectId} query={query}/>
                        </Col>
                        <Col data-testid={`query-list-qa-${query.id}`}><ExportQBButton query={query}/></Col>
                    </Row>
                </Col>
            </Row>
        }
    </Row>
}